/**
 * MenuPageBare
 * ────────────
 * Restaurant-style /menu view used by tenants that flip
 * `business.menu_layout = "compact"`. The page itself is intentionally
 * type-driven, bare-bones with NO inline images — guests click any
 * item to reveal a modal containing the photo, story, ingredients and
 * dietary information. Best for fine-dining where the menu is read,
 * not browsed.
 *
 *   - One hero image at the very top, then nothing but typography
 *   - Time-slot tabs at the top (All / Breakfast / Lunch / Dinner)
 *   - Categories listed top-to-bottom, items as price-aligned rows
 *   - Multi-size items show their dotted-leader price range
 *   - Items with add-ons show "Customise →" instead of "View"
 */
import { useState, useMemo } from "react";
import { Leaf, X } from "lucide-react";
import { money } from "@/lib/api";
import { useBusiness } from "@/context/BusinessContext";
import {
  useDietaryConflictMap, AllergenBadge, conflictWrapperClass,
} from "@/lib/useDietaryConflictMap";
import { useCart } from "@/context/CartContext";
import { useMenuData } from "@/lib/useMenuData";
import MenuSlotTabs from "@/components/MenuSlotTabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import AddonDialog from "@/components/AddonDialog";

const SIZE_LABELS = {
  sml: "Small", small: "Small", reg: "Regular", regular: "Regular",
  med: "Medium", medium: "Medium", lrg: "Large", large: "Large",
  cup: "Cup", pot: "Pot", mug: "Mug",
  "6oz":  "6oz", "12oz": "12oz", "16oz": "16oz",
  "14oz": "14oz", "20oz": "20oz",
  "glass-r": "Reg glass", "glass-l": "Lg glass",
  light: "Light", full: "Full",
};

function priceLabel(item, columnKeys) {
  const ks = Object.keys(item.prices || {});
  if (ks.length === 0) return money(Number(item.price) || 0);
  if (ks.length === 1) return money(Number(item.prices[ks[0]]) || 0);
  const sorted = columnKeys.length
    ? columnKeys.filter((k) => ks.includes(k))
    : ks.slice().sort();
  const vals = sorted.map((k) => Number(item.prices[k]) || 0);
  const lo = Math.min(...vals), hi = Math.max(...vals);
  return `${money(lo)} – ${money(hi)}`;
}

export default function MenuPageBare() {
  const { business } = useBusiness();
  const cart = useCart();
  const { cats, itemsByCat, loading, slot, setSlot } = useMenuData();
  // Flatten all items so the shared conflict-detector can build
  // a per-id Map in one pass. Then we re-look-up by id in the
  // row render below — keeps the loop O(N).
  const _allItems = useMemo(() => Object.values(itemsByCat || {}).flat(),
                                          [itemsByCat]);
  const conflictMap = useDietaryConflictMap(_allItems);
  const [open, setOpen] = useState(null);     // {item, category} for detail modal
  const [addonItem, setAddonItem] = useState(null);

  const dietaryChips = useMemo(() => {
    if (!open?.item) return [];
    const tags = (open.item.tags || []).map((t) => String(t).toLowerCase());
    return tags.filter((t) => /vegan|veget|gluten|dairy|nut|halal|kosher|spic/i.test(t));
  }, [open]);

  function addPlain(item) {
    if (item.addons && item.addons.length > 0) {
      const cat = cats.find((c) => c.id === item.category_id) || null;
      setAddonItem({ ...item, _category: cat });
      setOpen(null);
      return;
    }
    cart.addLine({
      item_id: item.id,
      name: item.name,
      base_price: Number(item.price) || 0,
      quantity: 1,
      selected_addons: [],
      notes: "",
      calories: Number(item.calories) || 0,
      tags: item.tags || [],
    });
    toast.success(`${item.name} added to cart`);
    setOpen(null);
  }

  if (loading) {
    return (
      <div data-testid="menu-bare-loading" className="max-w-3xl mx-auto px-4 py-24 text-center text-[#5C625E]">
        Loading the menu…
      </div>
    );
  }

  const hero = business?.hero_image_url || "";

  return (
    <div data-testid="menu-bare-page" className="bg-[#FDFBF7] pb-24">
      {hero && (
        <div className="relative w-full h-[34vh] md:h-[44vh] overflow-hidden">
          <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white px-4">
            <p className="text-xs uppercase tracking-[0.3em] mb-3 opacity-90">{business?.tagline || "Menu"}</p>
            <h1 className="font-serif-display text-5xl md:text-7xl leading-none">Menu</h1>
          </div>
        </div>
      )}

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
        <MenuSlotTabs slot={slot} onChange={setSlot} />
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
        {cats.map((cat) => {
          const rows = itemsByCat[cat.id] || [];
          if (rows.length === 0) return null;
          const colKeys = (cat.price_columns || []).map((c) => c.key);
          return (
            <section key={cat.id} data-testid={`menu-cat-${cat.id}`}>
              <header className="text-center mb-6">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#5A7247] font-bold">
                  {cat.kind === "beverage" ? "Drinks" : cat.kind === "wine" ? "Wine" : "Food"}
                </p>
                <h2 className="font-serif-display text-3xl md:text-4xl text-[#121614] mt-1">{cat.name}</h2>
              </header>
              <ul className="space-y-1">
                {rows.map((it) => {
                  const conflict = conflictMap.get(it.id);
                  return (
                  <li key={it.id}>
                    <button
                      type="button"
                      data-testid={`menu-item-${it.id}`}
                      onClick={() => !conflict?.hasAllergen
                                      && setOpen({ item: it, category: cat })}
                      disabled={conflict?.hasAllergen}
                      title={conflict?.reasons?.join(" · ") || ""}
                      className={`w-full flex items-baseline gap-3 py-3 text-left
                                      hover:bg-[#F3F0EA]/60 rounded-lg px-3
                                      transition-colors
                                      ${conflictWrapperClass(conflict)}
                                      ${conflict?.hasAllergen
                                          ? "cursor-not-allowed bg-rose-50/40"
                                          : ""}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-[#121614]">{it.name}</p>
                          <AllergenBadge conflict={conflict}
                              testId={`bare-${it.id}-allergen`} />
                        </div>
                        {it.description && (
                          <p className="text-sm text-[#5C625E] mt-0.5 line-clamp-1">{it.description}</p>
                        )}
                      </div>
                      <span
                        data-testid={`menu-price-${it.id}`}
                        className="font-medium tabular-nums text-[#121614] whitespace-nowrap"
                      >
                        {priceLabel(it, colKeys)}
                      </span>
                    </button>
                  </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </section>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent data-testid="menu-bare-detail" className="max-w-lg rounded-2xl p-0 overflow-hidden">
          {open && (
            <div>
              {open.item.image_url ? (
                <div className="aspect-[4/3] bg-[#F3F0EA]">
                  <img src={open.item.image_url} alt={open.item.name} className="w-full h-full object-cover" />
                </div>
              ) : null}
              <div className="p-6">
                <DialogTitle className="font-serif-display text-2xl">{open.item.name}</DialogTitle>
                {open.item.description && (
                  <DialogDescription className="text-sm text-[#5C625E] mt-2">
                    {open.item.description}
                  </DialogDescription>
                )}
                {open.item.long_description && (
                  <p className="text-sm leading-relaxed text-[#3D3D3D] mt-3 italic">{open.item.long_description}</p>
                )}
                {dietaryChips.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {dietaryChips.map((t) => (
                      <span
                        key={t}
                        data-testid={`detail-tag-${t.replace(/\s+/g, "-")}`}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] bg-[#5A7247]/10 text-[#5A7247] px-2 py-0.5 rounded-full font-bold"
                      >
                        <Leaf className="h-3 w-3" strokeWidth={2} />
                        {t}
                      </span>
                    ))}
                  </div>
                )}
                {Object.keys(open.item.prices || {}).length > 0 && (
                  <div className="mt-5" data-testid="bare-size-chips">
                    <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-[#5C625E] mb-2">Sizes — tap to add</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(open.category.price_columns || []).map((c) => {
                        const v = open.item.prices?.[c.key];
                        if (v == null) return null;
                        const label = SIZE_LABELS[c.key] || c.label || c.key;
                        return (
                          <button
                            key={c.key}
                            data-testid={`bare-size-chip-${open.item.id}-${c.key}`}
                            onClick={() => {
                              const variant = { ...open.item, id: `${open.item.id}__${c.key}`, name: `${open.item.name} (${label})`, price: Number(v), prices: undefined, size_chosen: label };
                              addPlain(variant);
                              toast.success(`${label} added`);
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#121614]/20 hover:border-[#121614] hover:bg-[#F3F0EA] text-[12px] font-semibold transition-colors"
                          >
                            <span>{label}</span>
                            <span className="tabular-nums text-[#5C625E]">{money(Number(v))}</span>
                            <span className="ml-0.5 w-4 h-4 rounded-full bg-[#121614] text-white text-[10px] grid place-items-center">+</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="mt-6 flex items-center justify-between gap-3">
                  <span className="font-bold tabular-nums text-xl">
                    {priceLabel(open.item, (open.category.price_columns || []).map((c) => c.key))}
                  </span>
                  <Button
                    data-testid="menu-bare-detail-add"
                    onClick={() => addPlain(open.item)}
                    className="bg-[#121614] hover:bg-black text-white rounded-full px-5"
                  >
                    {(open.item.addons || []).length > 0 ? "Customise →" : "Add to cart"}
                  </Button>
                </div>
              </div>
              <button
                type="button"
                data-testid="menu-bare-detail-close"
                aria-label="Close"
                onClick={() => setOpen(null)}
                className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center"
              >
                <X className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {addonItem && <AddonDialog item={addonItem} onClose={() => setAddonItem(null)} />}
    </div>
  );
}
